import { DateTime } from 'luxon';
import { useFrontendTool } from '@copilotkit/react-core';
import {
  Card,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core';

import { FilesSearchWidget } from 'types/dashboard_widget';

import { FILES_SEARCH_ICONS_MAP } from 'files/services/icons_map';
import { FilesSearchEntity, useFilesSearch } from 'files/hooks/useFilesSearch';
import { formatPretty } from 'services/string';
import { useModal } from 'hooks/useModal';

import { Icon } from 'components/Icon/IconDeprecated';
import { Pill } from 'components/Pill/Pill';
import { Thumbnail } from 'components/Thumbnail/Thumbnail';
import { Tooltip } from 'components/Tooltip/Tooltip';

import { AddToDashboardMenu } from './AddToDashboardMenu';

type SearchFilesArgs = {
  query?: string;
  searchEntities?: string;
  confidence?: number;
  timeRangeStart?: string;
  timeRangeStop?: string;
  cameraIds?: string;
  streamIds?: string;
  gatewayIds?: string;
  limit?: number;
};

type SearchFilesRenderProps = {
  status: 'complete' | 'executing' | 'inProgress';
  args: SearchFilesArgs;
};

function safeJsonParse<T>(json: string | undefined, fallback: T): T {
  if (!json) return fallback;
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

function SearchFilesRenderer({ status, args }: SearchFilesRenderProps) {
  const { open } = useModal();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const {
    query,
    searchEntities: searchEntitiesJson,
    confidence,
    timeRangeStart,
    timeRangeStop,
    cameraIds: cameraIdsJson,
    streamIds: streamIdsJson,
    gatewayIds: gatewayIdsJson,
    limit = 10,
  } = args;

  const searchEntities: FilesSearchEntity[] | undefined = safeJsonParse(
    searchEntitiesJson,
    undefined
  );
  const cameraIds: string[] | undefined = safeJsonParse(cameraIdsJson, undefined);
  const streamIds: string[] | undefined = safeJsonParse(streamIdsJson, undefined);
  const gatewayIds: string[] | undefined = safeJsonParse(gatewayIdsJson, undefined);

  const { data, isLoading, error } = useFilesSearch({
    params: {
      query,
      search_entities: searchEntities,
      confidence,
      created_ts_since: timeRangeStart,
      created_ts_until: timeRangeStop,
      camera_ids: cameraIds,
      stream_ids: streamIds,
      gateway_ids: gatewayIds,
      limit,
    },
    enabled: status === 'complete',
  });

  const createWidget = () => {
    const defaultLayout = { x: 0, y: 0, w: 6, h: 4 };

    return new FilesSearchWidget(
      crypto.randomUUID(),
      query || 'File Search',
      {
        query: query || '',
        search_entities: searchEntities || [
          'objects',
          'events',
          'texts',
          'trackables',
        ],
        confidence: confidence ?? 0.5,
        sort_by: 'created_at',
        camera_ids: cameraIds || [],
        stream_ids: streamIds || [],
        gateway_ids: gatewayIds || [],
      },
      defaultLayout
    );
  };

  if (status === 'inProgress' || status === 'executing' || isLoading) {
    return (
      <Card withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text c="dimmed" size="sm">
            Searching for files...
          </Text>
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
            {Array.from({ length: Math.min(limit, 6) }).map((_, i) => (
              <Skeleton key={i} height={100} radius="md" />
            ))}
          </SimpleGrid>
        </Stack>
      </Card>
    );
  }

  if (error) {
    return (
      <Card withBorder p="md" radius="md">
        <Text c="red" size="sm">
          Failed to search files: {error.message || 'Unknown error'}
        </Text>
      </Card>
    );
  }

  const files = data?.data || [];

  if (files.length === 0) {
    return (
      <Card withBorder p="md" radius="md">
        <Text size="sm" c="dimmed">
          No files found matching your search criteria.
        </Text>
      </Card>
    );
  }

  return (
    <Card withBorder p="md" radius="md" className="relative @container">
      <Stack gap="sm">
        <Title order={5}>
          Found {data?.data.length || files.length} file
          {files.length !== 1 ? 's' : ''}
        </Title>
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
          {files.map((file) => {
            const objectsWithIcon = file.objects
              .filter((object) => FILES_SEARCH_ICONS_MAP[object])
              .slice(0, 3);
            const objectsWithoutIcon = file.objects.filter(
              (object) => !objectsWithIcon.includes(object)
            );

            const [firstEvent, secondEvent, ...moreEvents] = file.events;

            return (
              <button
                key={file.file_id}
                onClick={() =>
                  open('files-search', {
                    defaultFile: {
                      id: file.file_id,
                      created_at: file.created_at,
                    },
                    defaultStreamId: file.stream_id,
                  })
                }
                className="flex cursor-pointer flex-col gap-1 rounded-md border border-neutral-200 bg-white p-2 text-left transition-colors hover:border-neutral-400 hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:hover:border-neutral-500 dark:hover:bg-neutral-700"
              >
                <Thumbnail
                  thumbnail_url={file.thumbnail_url}
                  mime_type={file.mime_type}
                  className="aspect-video w-full rounded"
                />
                <Text size="xs" c="dimmed">
                  {DateTime.fromISO(file.created_at)
                    .setZone(timezone)
                    .toLocaleString(DateTime.DATETIME_SHORT)}
                </Text>

                {/* Compact mode (default): icons only in one row */}
                <div className="flex flex-wrap items-center gap-1 @[500px]:hidden">
                  {objectsWithIcon.length > 0 && (
                    <Tooltip content={file.objects.join(', ')}>
                      <Icon
                        name={FILES_SEARCH_ICONS_MAP[objectsWithIcon[0]]}
                        size="small"
                      />
                    </Tooltip>
                  )}
                  {file.events.length > 0 && (
                    <Tooltip content={file.events.map(formatPretty).join(', ')}>
                      <Icon name="alarm" size="small" className="text-neutral-500" />
                    </Tooltip>
                  )}
                  {file.texts.length > 0 && (
                    <Tooltip content={file.texts.join(', ')}>
                      <Icon name="clipboard" size="small" className="text-neutral-500" />
                    </Tooltip>
                  )}
                </div>

                {/* Expanded mode: full content like FilesSearchDrawer */}
                <div className="hidden flex-col gap-1 @[500px]:flex">
                  {file.objects.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                      {objectsWithIcon.map((object) => (
                        <Tooltip key={object} content={object}>
                          <Icon name={FILES_SEARCH_ICONS_MAP[object]} size="small" />
                        </Tooltip>
                      ))}
                      {objectsWithoutIcon.length > 0 && (
                        <Tooltip content={objectsWithoutIcon.join(', ')}>
                          <span className="text-xs text-neutral-500">
                            +{objectsWithoutIcon.length}
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {file.events.length > 0 && (
                    <div className="flex items-center gap-1 text-neutral-500 dark:text-neutral-400">
                      <Icon name="alarm" size="small" />
                      <Pill size="small">{formatPretty(firstEvent)}</Pill>
                      {secondEvent && (
                        <Pill size="small">{formatPretty(secondEvent)}</Pill>
                      )}
                      {moreEvents.length > 0 && (
                        <Tooltip content={moreEvents.map(formatPretty).join(', ')}>
                          <span className="text-xs">+{moreEvents.length}</span>
                        </Tooltip>
                      )}
                    </div>
                  )}

                  {file.texts.length > 0 && (
                    <div className="flex items-center gap-1 text-neutral-500 dark:text-neutral-400">
                      <Icon name="clipboard" size="small" />
                      <Tooltip content={file.texts.join(', ')}>
                        <span className="max-w-[150px] truncate text-xs">
                          {file.texts[0]}
                          {file.texts.length > 1 && ` +${file.texts.length - 1}`}
                        </span>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </SimpleGrid>

        {data && data.data.length > files.length && (
          <Text size="xs" c="dimmed">
            Showing {files.length} of {data.data.length} results
          </Text>
        )}
      </Stack>
      <div className="absolute bottom-2 right-2">
        <AddToDashboardMenu createWidget={createWidget} />
      </div>
    </Card>
  );
}

export function useSearchFilesTool() {
  const now = new Date().toISOString();

  useFrontendTool(
    {
      name: 'searchFiles',
      description: `Search for recordings (video clips, images) using natural language queries or structured filters. Can search for objects detected, with optional attributes (like "car", "person", "license_plate.ABCDE"), events, text (OCR), and watchlist items. Results show thumbnails that users can click to view. IMPORTANT: The current date and time is ${now}. Use this for relative time calculations like "today", "this week", "last 7 days", etc.`,
      parameters: [
        {
          name: 'query',
          type: 'string',
          description:
            'Natural language search query to find files (e.g., "red car", "person entering", "license plate ABC123"). Use * prefix and postfix to search for partial matches.',
          required: false,
        },
        {
          name: 'searchEntities',
          type: 'string',
          description:
            'JSON array of entity types to search in: ["objects", "events", "texts", "trackables", "vectors"]. Defaults to all.',
          required: false,
        },
        {
          name: 'confidence',
          type: 'number',
          description:
            'Minimum confidence threshold for search results (0 to 1). Defaults to 0.5.',
          required: false,
        },
        {
          name: 'timeRangeStart',
          type: 'string',
          description:
            'Start of time range in ISO 8601 format (e.g., "2024-01-01T00:00:00Z")',
          required: false,
        },
        {
          name: 'timeRangeStop',
          type: 'string',
          description:
            'End of time range in ISO 8601 format (e.g., "2024-01-31T23:59:59Z")',
          required: false,
        },
        {
          name: 'cameraIds',
          type: 'string',
          description: 'JSON array of camera IDs to filter by',
          required: false,
        },
        {
          name: 'streamIds',
          type: 'string',
          description: 'JSON array of stream IDs to filter by',
          required: false,
        },
        {
          name: 'gatewayIds',
          type: 'string',
          description: 'JSON array of gateway IDs to filter by',
          required: false,
        },
        {
          name: 'limit',
          type: 'number',
          description: 'Maximum number of results to return. Defaults to 10.',
          required: false,
        },
      ],
      handler: async (args: SearchFilesArgs) => {
        return { ...args, message: 'File search results displayed to user' };
      },
      render: ({ status, args }) => (
        <SearchFilesRenderer status={status} args={args as SearchFilesArgs} />
      ),
    },
    []
  );
}
